import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { forwardRef, useMemo, createElement, useState, useLayoutEffect, useEffect, useCallback, isValidElement } from 'react';
import { CheckmarkIcon } from '@flex-design-system/legacy-flex-icons';
import { useMergedCSS, styled } from '@flex-design-system/stitches-react';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import usePopperPresence from '../../../hooks/usePopperPresence.mjs';
import '../../../utils/styles/flex.mjs';
import Stack from '../../../utils/styles/stack.mjs';
import { Flex } from '../../layout/Flex.mjs';
import { Legacy_Dropdown } from '../../legacy-dropdown/Dropdown.mjs';
import BubbleSelect from '../BubbleSelect.mjs';
import { SELECT_SEARCH_DATA_ROLE } from '../constants/dataRoles.mjs';
import useNearestSeparatorHidden from '../hooks/useNearestSeparatorHidden.mjs';
import useOptionObserver from '../hooks/useOptionCount.mjs';
import SearchEmptyImpl from '../SearchEmptyImpl.mjs';
import { SingleSelectOptionItem, StyledAffixRoot } from '../styled.mjs';
import { isMatchedItem } from '../utils/item.mjs';
import { useSelectContext, useSelectSearchContext, Collection, SelectProvider, SelectSearchProvider } from './context.mjs';
import SearchInput from './SearchInput.mjs';
import StaticInput from './StaticInput.mjs';

const InternalSelect = (props) => {
  const {
    open: openProp,
    defaultOpen,
    value: valueProp,
    defaultValue,
    onValueChange,
    onOpenChange,
    onClose,
    children,
    name,
    autoComplete,
    searchValue,
    searchDefaultValue,
    onSearchValueChange,
    disableFormAutoFill = false
  } = props;
  const [hasEmptyView, setHasEmptyView] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [trigger, setTrigger] = useState(null);
  const isFormControl = trigger ? Boolean(trigger.closest("form")) : true;
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  const [value = "", setValue] = useControllableState({
    prop: valueProp,
    defaultProp: defaultValue,
    onChange: onValueChange
  });
  return /* @__PURE__ */ jsx(
    Legacy_Dropdown,
    {
      ...props,
      open,
      onOpenChange: setOpen,
      defaultOpen,
      children: /* @__PURE__ */ jsx(
        SelectSearchRoot,
        {
          value: searchValue,
          defaultValue: searchDefaultValue,
          onChangeValue: onSearchValueChange,
          children: /* @__PURE__ */ jsxs(
            SelectProvider,
            {
              value,
              onValueChange: setValue,
              open,
              onOpenChange: setOpen,
              trigger,
              onTriggerChange: setTrigger,
              inputFocused,
              onInputFocusChange: setInputFocused,
              hasEmptyView,
              onHasEmptyViewChange: setHasEmptyView,
              onClose,
              children: [
                children,
                !disableFormAutoFill && isFormControl ? /* @__PURE__ */ jsx(VisuallyHidden, { "data-role": "visually-hidden", children: /* @__PURE__ */ jsx(
                  BubbleSelect,
                  {
                    "aria-hidden": true,
                    tabIndex: -1,
                    name,
                    autoComplete,
                    value,
                    onChange: (event) => setValue(event.target.value)
                  }
                ) }) : null
              ]
            }
          )
        }
      )
    }
  );
};
const SelectSubMenu = (props) => {
  const { onValueChange, children, ...subMenuProps } = props;
  const context = useSelectContext();
  const searchContext = useSelectSearchContext();
  return /* @__PURE__ */ jsx(
    InternalSelect,
    {
      ...subMenuProps,
      value: context.value,
      onValueChange: composeEventHandlers(onValueChange, context.onValueChange),
      searchValue: searchContext.searchValue,
      onSearchValueChange: searchContext.onSearchValueChange,
      children: /* @__PURE__ */ jsx(Legacy_Dropdown.Sub, { children })
    }
  );
};
const SelectSearchRoot = ({
  children,
  defaultValue,
  value: valueFromProps,
  onChangeValue
}) => {
  const [value = "", setValue] = useControllableState({
    prop: valueFromProps,
    defaultProp: defaultValue,
    onChange: onChangeValue
  });
  const [renderEmpty, setRenderEmpty] = useState(false);
  return /* @__PURE__ */ jsx(
    SelectSearchProvider,
    {
      searchValue: value,
      onSearchValueChange: setValue,
      renderEmpty,
      onRenderEmptyChange: setRenderEmpty,
      children
    }
  );
};
const SelectTrigger = forwardRef(
  (props, forwardedRef) => {
    const { children, ...restProps } = props;
    const context = useSelectContext();
    const composedRefs = useComposedRefs(forwardedRef, context.onTriggerChange);
    return /* @__PURE__ */ jsx(Legacy_Dropdown.Trigger, { type: "button", ...restProps, ref: composedRefs, children });
  }
);
const SelectSubMenuTrigger = forwardRef((props, ref) => {
  const {
    children,
    textValue = "",
    disabled = false,
    hidden: hiddenFromProps,
    ...submenuTriggerItemProps
  } = props;
  const { searchValue } = useSelectSearchContext();
  const hidden = useMemo(() => {
    const hiddenFromContext = searchValue !== "" && !textValue.includes(searchValue);
    return hiddenFromProps ?? hiddenFromContext;
  }, [hiddenFromProps, textValue, searchValue]);
  return /* @__PURE__ */ jsx(
    Collection.ItemSlot,
    {
      value: textValue,
      disabled,
      scope: void 0,
      children: /* @__PURE__ */ createElement(
        Legacy_Dropdown.SubTrigger,
        {
          ...submenuTriggerItemProps,
          key: searchValue,
          ref,
          hidden
        },
        children
      )
    }
  );
});
const DEFAULT_OPTION_COUNT_STATE = -1;
const SelectContentImpl = forwardRef(
  (props, ref) => {
    const {
      children,
      align = "start",
      affix,
      zIndex,
      css: cssFromProps,
      ...restProps
    } = props;
    const { open, value, hasEmptyView, onClose } = useSelectContext();
    const { onRenderEmptyChange, renderEmpty } = useSelectSearchContext();
    const [contentElement, setContentElement] = useState(null);
    const { ref: presenceRef } = usePopperPresence({
      open,
      onPresenceChange: (isPresent) => !isPresent && onClose?.()
    });
    const composedRef = useComposedRefs(
      ref,
      presenceRef,
      (element) => setContentElement(element)
    );
    const optionCount = useOptionObserver(contentElement, {
      fallbackState: DEFAULT_OPTION_COUNT_STATE
    });
    useLayoutEffect(() => {
      if (open && optionCount !== DEFAULT_OPTION_COUNT_STATE) {
        onRenderEmptyChange(optionCount === 0);
      }
    }, [onRenderEmptyChange, optionCount, open]);
    const nothingToDraw = renderEmpty && !hasEmptyView;
    const css = useMergedCSS(
      cssFromProps,
      nothingToDraw ? {
        py: 0
      } : void 0
    );
    return /* @__PURE__ */ jsx(
      Legacy_Dropdown.Content,
      {
        ...restProps,
        css,
        affix,
        align,
        role: "listbox",
        zIndex,
        ref: composedRef,
        children: /* @__PURE__ */ jsx(Legacy_Dropdown.RadioGroup, { value: `${value}`, children: /* @__PURE__ */ jsx("div", { children }) })
      }
    );
  }
);
const SelectContent = forwardRef(
  (props, ref) => {
    const { children, ...restProps } = props;
    return /* @__PURE__ */ jsx(Collection.Provider, { scope: void 0, children: /* @__PURE__ */ jsx(Collection.Slot, { scope: void 0, children: /* @__PURE__ */ jsx(SelectContentImpl, { ...restProps, role: "listbox", ref, children }) }) });
  }
);
const SelectSearchEmpty = forwardRef(
  ({ children, ...props }, ref) => {
    const { onHasEmptyViewChange } = useSelectContext();
    const { renderEmpty, searchValue } = useSelectSearchContext();
    useEffect(() => {
      onHasEmptyViewChange(true);
      return () => {
        onHasEmptyViewChange(false);
      };
    }, [onHasEmptyViewChange]);
    return renderEmpty ? /* @__PURE__ */ jsx("div", { "data-role": SELECT_SEARCH_DATA_ROLE, ref, ...props, children: typeof children === "function" ? children(searchValue) : children ?? /* @__PURE__ */ jsx(SearchEmptyImpl, {}) }) : null;
  }
);
function SelectValue(props) {
  const context = useSelectContext();
  return /* @__PURE__ */ jsx(Fragment, { children: props.children({ value: context.value }) });
}
function SelectCapturedActiveValue(props) {
  const context = useSelectContext();
  const capturedActiveValue = useMemo(
    () => context.value,
    // 셀렉트가 열리는 시점에 선택 되어 있는 옵션값을 캡쳐 합니다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  return /* @__PURE__ */ jsx(Fragment, { children: props.children({ value: capturedActiveValue }) });
}
const SelectOption = forwardRef(
  ({
    children,
    disabled = false,
    value,
    textValue,
    hidden: hiddenFromProps,
    ...props
  }, ref) => {
    const { searchValue } = useSelectSearchContext();
    const { inputFocused, onValueChange } = useSelectContext();
    const valueString = useMemo(() => value.toString(), [value]);
    const hidden = useMemo(() => {
      const hiddenFromContext = searchValue !== "" && !isMatchedItem({ textValue, value: valueString }, searchValue);
      return hiddenFromProps ?? hiddenFromContext;
    }, [searchValue, textValue, valueString, hiddenFromProps]);
    const handleSelect = useCallback(() => {
      onValueChange(value);
    }, [onValueChange, value]);
    return /* @__PURE__ */ jsx(
      Collection.ItemSlot,
      {
        value: valueString,
        textValue,
        disabled,
        scope: void 0,
        children: /* @__PURE__ */ jsx(
          SingleSelectOptionItem,
          {
            ...props,
            ref,
            hidden,
            disabled,
            value: valueString,
            textValue,
            onSelect: composeEventHandlers(props.onSelect, handleSelect, {
              checkForDefaultPrevented: false
            }),
            onPointerMove: composeEventHandlers(props.onPointerMove, (e) => {
              if (inputFocused) {
                e.preventDefault();
              }
            }),
            onPointerLeave: composeEventHandlers(props.onPointerLeave, (e) => {
              if (inputFocused) {
                e.preventDefault();
              }
            }),
            role: "option",
            children: /* @__PURE__ */ jsx(StyledOptionItemContent, { direction: "column", align: "start", children })
          }
        )
      }
    );
  }
);
const StyledOptionItemContent = styled(Flex, {
  width: "100%",
  lineHeight: 1.2
});
const OptionTitle = forwardRef(
  ({ children, selectIndicator, ...props }, ref) => {
    const indicator = useMemo(() => {
      return isValidElement(selectIndicator) ? selectIndicator : /* @__PURE__ */ jsx(SelectOptionIndicator, { style: { marginLeft: "auto" }, children: /* @__PURE__ */ jsx(CheckmarkIcon, { color: "green" }) });
    }, [selectIndicator]);
    return /* @__PURE__ */ jsx(Legacy_Dropdown.ItemTitle, { ref, rightSlot: indicator, ...props, children });
  }
);
const OptionDescription = forwardRef(({ children, ...props }, ref) => {
  return /* @__PURE__ */ jsx(Legacy_Dropdown.ItemDescription, { ref, ...props, children });
});
const SelectOptionIndicator = ({ children, ...props }) => {
  return /* @__PURE__ */ jsx(StyledIndicator, { ...props, children });
};
const StyledIndicator = styled(Legacy_Dropdown.ItemIndicator, {
  width: 24
});
function SelectSeparator(props) {
  const { renderEmpty } = useSelectSearchContext();
  return /* @__PURE__ */ jsx(StyledDivider, { ...props, hidden: props.hidden ?? renderEmpty });
}
const StyledDivider = styled(Legacy_Dropdown.Divider, {
  "&[hidden]": {
    display: "none"
  }
});
const AffixRoot = forwardRef((props, ref) => {
  const { hasEmptyView } = useSelectContext();
  const { renderEmpty } = useSelectSearchContext();
  const nothingBelow = renderEmpty && !hasEmptyView;
  return /* @__PURE__ */ jsx(StyledAffixRoot, { bordered: !nothingBelow, ...props, ref });
});
const OPTION_COUNT_FALLBACK_STATE = -1;
const SelectGroup = forwardRef((props, ref) => {
  const [groupElement, setGroupElement] = useState(null);
  const optionCount = useOptionObserver(groupElement, {
    fallbackState: OPTION_COUNT_FALLBACK_STATE
  });
  const { title, ...groupProps } = props;
  const composedRef = useComposedRefs(ref, (node) => setGroupElement(node));
  const hasOptionElements = optionCount !== 0;
  useNearestSeparatorHidden(groupElement);
  return /* @__PURE__ */ jsx(
    Legacy_Dropdown.Group,
    {
      ref: composedRef,
      title: hasOptionElements ? title : void 0,
      ...groupProps,
      hidden: !hasOptionElements
    }
  );
});
const SelectSubContent = forwardRef(
  (props, ref) => {
    const { children, ...subContentProps } = props;
    const context = useSelectContext();
    return /* @__PURE__ */ jsx(Legacy_Dropdown.SubContent, { ...subContentProps, ref, children: /* @__PURE__ */ jsx(Legacy_Dropdown.RadioGroup, { value: `${context.value}`, children }) });
  }
);
const SelectOption_legacy = forwardRef(
  ({
    children,
    disabled = false,
    value,
    textValue,
    selectIndicator,
    hidden: hiddenFromProps,
    ...props
  }, ref) => {
    const { searchValue } = useSelectSearchContext();
    const { inputFocused, onValueChange } = useSelectContext();
    const valueString = useMemo(() => value.toString(), [value]);
    const hidden = useMemo(() => {
      const hiddenFromContext = searchValue !== "" && !isMatchedItem({ textValue, value: value.toString() }, searchValue);
      return hiddenFromProps ?? hiddenFromContext;
    }, [hiddenFromProps, textValue, searchValue, value]);
    const indicator = useMemo(() => {
      return isValidElement(selectIndicator) ? selectIndicator : /* @__PURE__ */ jsx(SelectOptionIndicator, { style: { marginLeft: "auto" }, children: /* @__PURE__ */ jsx(CheckmarkIcon, { color: "green" }) });
    }, [selectIndicator]);
    const handleSelect = useCallback(() => {
      onValueChange(value);
    }, [onValueChange, value]);
    const child = useMemo(() => {
      return /* @__PURE__ */ jsx(
        SingleSelectOptionItem,
        {
          ...props,
          ref,
          hidden,
          disabled,
          value: valueString,
          textValue,
          onSelect: composeEventHandlers(props.onSelect, handleSelect, {
            checkForDefaultPrevented: false
          }),
          onPointerMove: composeEventHandlers(props.onPointerMove, (e) => {
            if (inputFocused) {
              e.preventDefault();
            }
          }),
          onPointerLeave: composeEventHandlers(props.onPointerLeave, (e) => {
            if (inputFocused) {
              e.preventDefault();
            }
          }),
          role: "option",
          children: /* @__PURE__ */ jsxs(Stack.Horizontal, { style: { width: "100%" }, children: [
            children,
            indicator
          ] })
        }
      );
    }, [
      children,
      disabled,
      handleSelect,
      hidden,
      indicator,
      inputFocused,
      props,
      ref,
      textValue,
      valueString
    ]);
    return /* @__PURE__ */ jsx(
      Collection.ItemSlot,
      {
        value: valueString,
        textValue,
        disabled,
        scope: void 0,
        children: child
      }
    );
  }
);
const OptionTitle_legacy = forwardRef(({ children, ...props }, ref) => {
  return /* @__PURE__ */ jsx(Legacy_Dropdown.ItemTitle, { ref, ...props, children });
});
const Legacy_Select = InternalSelect;
Legacy_Select.Trigger = SelectTrigger;
Legacy_Select.Sub = SelectSubMenu;
Legacy_Select.SubTrigger = SelectSubMenuTrigger;
Legacy_Select.SubContent = SelectSubContent;
Legacy_Select.Value = SelectValue;
Legacy_Select.CapturedActiveValue = SelectCapturedActiveValue;
Legacy_Select.Content = SelectContent;
Legacy_Select.Option = SelectOption;
Legacy_Select.Group = SelectGroup;
Legacy_Select.Divider = SelectSeparator;
Legacy_Select.OptionTitle = OptionTitle;
Legacy_Select.OptionDescription = OptionDescription;
Legacy_Select.SearchInput = SearchInput;
Legacy_Select.StaticInput = StaticInput;
Legacy_Select.SearchEmpty = SelectSearchEmpty;
Legacy_Select.Aside = Legacy_Dropdown.Aside;
Legacy_Select.AffixRoot = AffixRoot;
Legacy_Select.OptionIndicator = SelectOptionIndicator;
Legacy_Select.Option_legacy = SelectOption_legacy;
Legacy_Select.OptionTitle_legacy = OptionTitle_legacy;

export { Legacy_Select };
//# sourceMappingURL=Select.mjs.map
